import React, { useState } from 'react';
import { useLeague } from '@shootout-comps/core';
import type { League } from '@shootout-comps/types';

interface SendLeagueNotificationDialogProps {
  league: League;
  onClose: () => void;
  onSent: (updated: League) => void;
}

function formatSentAt(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// Owner/admin dialog for broadcasting a push notification to every linked player in the league
// (or one division of it) who has the mobile app installed and notifications enabled. Also shows
// the league's notification history (capped at the most recent 50 sends server-side).
export default function SendLeagueNotificationDialog({ league, onClose, onSent }: SendLeagueNotificationDialogProps) {
  const { sendLeagueNotification } = useLeague();

  const [divisionId, setDivisionId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [justSent, setJustSent] = useState(false);

  const log = league.notificationLog ?? [];
  const lastSent = log[0];

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true); setError(''); setJustSent(false);
    try {
      const updated = await sendLeagueNotification(league._id, { title: title.trim(), body: body.trim(), divisionId: divisionId ?? undefined });
      onSent(updated);
      setJustSent(true);
      setTitle(''); setBody('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 300,
      background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem', animation: 'fadeInSendNotification 0.15s ease',
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--baize), var(--felt))',
        border: '2px solid rgba(155,89,182,0.4)', borderRadius: '14px',
        padding: 'clamp(1.5rem,5vw,2rem)', width: '100%', maxWidth: '460px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)', animation: 'slideUpSendNotification 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', lineHeight: 1, color: '#af7ac5' }}>
            📣 Send Notification
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
        </div>
        <p style={{ color: 'var(--chalk-dim)', fontSize: '0.78rem', lineHeight: 1.5, marginBottom: '1.1rem' }}>
          Sends a push notification to every player in this league who's linked to an account and has the app installed.
        </p>

        {league.divisions.length > 0 && (
          <>
            <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Audience</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
              <button
                onClick={() => setDivisionId(null)}
                style={{
                  borderRadius: '20px', padding: '0.35rem 0.85rem', fontSize: '0.78rem', cursor: 'pointer',
                  background: divisionId === null ? 'rgba(201,168,76,0.15)' : 'transparent',
                  border: `1px solid ${divisionId === null ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.15)'}`,
                  color: divisionId === null ? 'var(--gold)' : 'var(--chalk-dim)', fontWeight: divisionId === null ? 600 : 400,
                }}
              >
                Whole league
              </button>
              {league.divisions.map(d => (
                <button
                  key={d._id}
                  onClick={() => setDivisionId(d._id)}
                  style={{
                    borderRadius: '20px', padding: '0.35rem 0.85rem', fontSize: '0.78rem', cursor: 'pointer',
                    background: divisionId === d._id ? 'rgba(201,168,76,0.15)' : 'transparent',
                    border: `1px solid ${divisionId === d._id ? 'rgba(201,168,76,0.4)' : 'rgba(255,255,255,0.15)'}`,
                    color: divisionId === d._id ? 'var(--gold)' : 'var(--chalk-dim)', fontWeight: divisionId === d._id ? 600 : 400,
                  }}
                >
                  {d.name}
                </button>
              ))}
            </div>
          </>
        )}

        <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Title</label>
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Fixture Postponed"
          maxLength={100}
          style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none', marginBottom: '0.9rem', boxSizing: 'border-box' }}
        />

        <label style={{ display: 'block', fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Message</label>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="e.g. Tonight's match vs Rovers is off due to weather."
          maxLength={300}
          rows={4}
          style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: 'var(--chalk)', fontSize: '0.85rem', outline: 'none', marginBottom: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
        />

        {error && (
          <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid var(--red)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: '#e74c3c', fontSize: '0.78rem', marginBottom: '0.9rem' }}>
            {error}
          </div>
        )}

        {justSent && lastSent && (
          <div style={{ background: 'rgba(46,204,113,0.12)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '8px', padding: '0.55rem 0.75rem', color: '#2ecc71', fontSize: '0.8rem', marginBottom: '0.9rem' }}>
            Sent to {lastSent.sent} device{lastSent.sent === 1 ? '' : 's'} across {lastSent.recipients} player{lastSent.recipients === 1 ? '' : 's'}.
          </div>
        )}

        <button
          onClick={handleSend}
          disabled={sending || !title.trim() || !body.trim()}
          style={{
            width: '100%', background: 'linear-gradient(135deg, var(--gold), var(--gold-light))', color: 'var(--baize-dark)',
            border: 'none', borderRadius: '8px', padding: '0.65rem', fontSize: '0.88rem', fontWeight: 700,
            cursor: sending || !title.trim() || !body.trim() ? 'not-allowed' : 'pointer',
            opacity: sending || !title.trim() || !body.trim() ? 0.6 : 1,
          }}
        >
          {sending ? '…' : 'Send Notification'}
        </button>

        {log.length > 0 && (
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.9rem' }}>
            <button
              onClick={() => setShowHistory(s => !s)}
              style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '0.78rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              {showHistory ? '▾' : '▸'} History ({log.length})
            </button>
            {showHistory && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.75rem', maxHeight: '220px', overflowY: 'auto' }}>
                {log.map(entry => (
                  <div key={entry._id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '0.55rem 0.7rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem', fontSize: '0.7rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>
                      <span>{formatSentAt(entry.sentAt)} · {entry.sentByUsername}</span>
                      <span>{entry.divisionName ?? 'Whole league'}</span>
                    </div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: '0.1rem' }}>{entry.title}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', marginBottom: '0.2rem' }}>{entry.body}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)' }}>
                      {entry.sent} device{entry.sent === 1 ? '' : 's'} / {entry.recipients} player{entry.recipients === 1 ? '' : 's'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeInSendNotification  { from{opacity:0} to{opacity:1} }
        @keyframes slideUpSendNotification { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}
